import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../config';
import { FileText, Plus, Calendar, User, CheckCircle, Clock, XCircle, Download, Loader2 } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import './Performance.css';

function EvaluationsList({ user, onNavigate, hideHeader = false }) {
  const { t } = useTranslation();
  const [evaluations, setEvaluations] = useState([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    evaluation_type: '',
    status: ''
  });
  
  const [newEvaluation, setNewEvaluation] = useState({
    employee_id: '',
    evaluation_type: 'annual',
    title: '',
    description: '',
    evaluation_period_start: '',
    evaluation_period_end: '',
    evaluation_date: ''
  });

  const [createLoading, setCreateLoading] = useState(false);
  const [criteria, setCriteria] = useState([]);
  const [newCriterion, setNewCriterion] = useState({
    criterion_name: '',
    criterion_category: 'technical',
    description: '',
    score: 0,
    rating: '',
    weight: 1.0,
    evaluator_notes: ''
  });

  const canCreateEvaluation = ['admin', 'manager', 'employee'].includes(user?.role);

  const saveDebounceRef = useRef(null);
  const selectedEvaluationRef = useRef(selectedEvaluation);
  useEffect(() => {
    selectedEvaluationRef.current = selectedEvaluation;
  }, [selectedEvaluation]);

  useEffect(() => {
    loadEvaluations();
    if (canCreateEvaluation) {
      loadEmployees();
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showDetailModal && saveDebounceRef.current) {
      clearTimeout(saveDebounceRef.current);
      saveDebounceRef.current = null;
    }
  }, [showDetailModal]);

  // Charger les employés quand le modal s'ouvre
  useEffect(() => {
    if (showNewModal && canCreateEvaluation) {
      loadEmployees();
    }
  }, [showNewModal, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmployees = async () => {
    try {
      console.log('🔄 [EVALUATIONS] Chargement des employés...');
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ [EVALUATIONS] Token manquant');
        alert('Session expirée. Veuillez vous reconnecter.');
        window.location.href = '/?auth=true';
        return;
      }
      
      const res = await fetch(`${API_BASE}/api/employees?all=true&active=all&allRoles=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json().catch(() => ({}));
      const errMsg = data.error || data.message || '';
      
      if (res.status === 401) {
        console.error('❌ [EVALUATIONS] Non autorisé (employees):', res.status);
        localStorage.removeItem('token');
        alert(errMsg || 'Votre session a expiré. Veuillez vous reconnecter.');
        window.location.href = '/?auth=true';
        return;
      }
      if (res.status === 403) {
        if (errMsg.toLowerCase().includes('abonnement') || errMsg.toLowerCase().includes('subscription')) {
          alert(errMsg);
          setEmployees([]);
          return;
        }
        if (errMsg.toLowerCase().includes('non trouvé') || errMsg.toLowerCase().includes('not found')) {
          setEmployees([]);
          return;
        }
        console.error('❌ [EVALUATIONS] Accès refusé (employees):', res.status, errMsg);
        localStorage.removeItem('token');
        alert(errMsg || 'Votre session a expiré. Veuillez vous reconnecter.');
        window.location.href = '/?auth=true';
        return;
      }
      
      if (!res.ok) {
        console.error('❌ [EVALUATIONS] Erreur API employees:', res.status, res.statusText);
        setEmployees([]);
        return;
      }
      console.log('📥 [EVALUATIONS] Réponse API employees:', data);
      
      // ✅ CORRECTION : Gérer la structure paginée (data.employees ou data.data)
      if (data.success) {
        const employeesList = data.employees || data.data || [];
        if (Array.isArray(employeesList) && employeesList.length > 0) {
          // Mapper les employés pour s'assurer d'avoir first_name et last_name
          const mappedEmployees = employeesList.map(emp => ({
          id: emp.id,
          first_name: emp.firstName || emp.first_name || emp.fullName?.split(' ')[0] || '',
          last_name: emp.lastName || emp.last_name || emp.fullName?.split(' ').slice(1).join(' ') || '',
          fullName: emp.fullName || `${emp.firstName || emp.first_name || ''} ${emp.lastName || emp.last_name || ''}`.trim()
        })).filter(emp => emp.first_name || emp.last_name); // Filtrer les employés sans nom
        
            console.log('✅ [EVALUATIONS] Employés mappés:', mappedEmployees.length, mappedEmployees);
            setEmployees(mappedEmployees);
          } else {
            console.warn('⚠️ [EVALUATIONS] Aucun employé trouvé ou format invalide:', employeesList);
            setEmployees([]);
          }
        } else {
          console.warn('⚠️ [EVALUATIONS] Réponse API sans success ou structure invalide:', data);
          setEmployees([]);
        }
    } catch (error) {
      console.error('❌ [EVALUATIONS] Erreur chargement employés:', error);
      
      // Si c'est une erreur réseau, ne pas rediriger
      if (error.message && error.message.includes('fetch')) {
        console.error('❌ [EVALUATIONS] Erreur réseau:', error.message);
        setEmployees([]);
        return;
      }
      
      // Pour les autres erreurs, vérifier si c'est lié à l'authentification
      setEmployees([]);
    }
  };

  const loadEvaluations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ [EVALUATIONS] Token manquant');
        alert('Session expirée. Veuillez vous reconnecter.');
        window.location.href = '/?auth=true';
        return;
      }
      
      const params = new URLSearchParams();
      
      if (filters.evaluation_type) params.append('evaluation_type', filters.evaluation_type);
      if (filters.status) params.append('status', filters.status);
      
      const res = await fetch(`${API_BASE}/api/performance/evaluations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json().catch(() => ({}));
      const errMsg = data.error || data.message || '';
      
      if (res.status === 401) {
        console.error('❌ [EVALUATIONS] Non autorisé:', res.status);
        localStorage.removeItem('token');
        alert(errMsg || 'Votre session a expiré. Veuillez vous reconnecter.');
        window.location.href = '/?auth=true';
        return;
      }
      if (res.status === 403) {
        if (errMsg.toLowerCase().includes('abonnement') || errMsg.toLowerCase().includes('subscription')) {
          alert(errMsg);
          setEvaluations([]);
          setLoading(false);
          return;
        }
        if (errMsg.toLowerCase().includes('non trouvé') || errMsg.toLowerCase().includes('not found')) {
          setEvaluations([]);
          setLoading(false);
          return;
        }
        console.error('❌ [EVALUATIONS] Accès refusé:', res.status, errMsg);
        localStorage.removeItem('token');
        alert(errMsg || 'Votre session a expiré. Veuillez vous reconnecter.');
        window.location.href = '/?auth=true';
        return;
      }
      
      if (!res.ok) throw new Error(t('performance.error_loading'));
      setEvaluations(data.evaluations || []);
    } catch (error) {
      console.error('Erreur chargement évaluations:', error);
      
      // Ne pas afficher d'alerte si c'est une redirection
      if (!error.message || !error.message.includes('redirect')) {
        alert(t('performance.error_loading'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEvaluationDetail = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/evaluations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error(t('performance.error_loading'));
      
      const data = await res.json();
      setSelectedEvaluation(data.evaluation);
      setCriteria(data.criteria || []);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Erreur chargement détail:', error);
      alert(t('performance.error_loading'));
    }
  };

  const handleCreateEvaluation = async () => {
    try {
      if (!newEvaluation.employee_id || !newEvaluation.title || !newEvaluation.evaluation_period_start || !newEvaluation.evaluation_period_end) {
        alert(t('performance.fill_required'));
        return;
      }
      setCreateLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/evaluations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEvaluation)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || t('performance.error_creating'));
      }

      setShowNewModal(false);
      setNewEvaluation({
        employee_id: '',
        evaluation_type: 'annual',
        title: '',
        description: '',
        evaluation_period_start: '',
        evaluation_period_end: '',
        evaluation_date: ''
      });
      loadEvaluations();
      alert(t('performance.created_success'));
    } catch (error) {
      console.error('Erreur création évaluation:', error);
      alert(error.message || 'Erreur lors de la création');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAddCriterion = async () => {
    try {
      if (!newCriterion.criterion_name || newCriterion.score === undefined) {
        alert(t('performance.name_score_required'));
        return;
      }

      if (!selectedEvaluation) return;

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/evaluations/${selectedEvaluation.id}/criteria`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCriterion)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || t('performance.error_adding_criterion'));
      }

      const data = await res.json();
      const addedCriterion = data.criterion;

      // Optimistically append new criterion so previous ones stay visible
      if (addedCriterion) {
        setCriteria(prev => [...prev, {
          id: addedCriterion.id,
          criterion_name: addedCriterion.criterion_name || newCriterion.criterion_name,
          criterion_category: addedCriterion.criterion_category || newCriterion.criterion_category,
          description: addedCriterion.description || newCriterion.description,
          score: addedCriterion.score ?? newCriterion.score,
          rating: addedCriterion.rating || newCriterion.rating,
          weight: addedCriterion.weight ?? newCriterion.weight,
          evaluator_notes: addedCriterion.evaluator_notes || newCriterion.evaluator_notes
        }]);
      }

      setNewCriterion({
        criterion_name: '',
        criterion_category: 'technical',
        description: '',
        score: 0,
        rating: '',
        weight: 1.0,
        evaluator_notes: ''
      });

      // Reload from server to keep list in sync
      await loadEvaluationDetail(selectedEvaluation.id);
      alert(t('performance.criterion_added_success'));
    } catch (error) {
      console.error('Erreur ajout critère:', error);
      alert(error.message || 'Erreur lors de l\'ajout');
    }
  };

  const TEXT_FIELDS = ['employee_self_assessment', 'evaluator_comments', 'employee_comments', 'action_plan'];
  const isTextField = (f) => TEXT_FIELDS.includes(f);

  const handleUpdateEvaluation = async (field, value) => {
    if (!selectedEvaluation) return;

    setSelectedEvaluation((prev) => ({ ...prev, [field]: value }));

    const doPut = async () => {
      const ev = selectedEvaluationRef.current;
      if (!ev) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/api/performance/evaluations/${ev.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ [field]: ev[field] })
        });
        if (!res.ok) throw new Error('Erreur lors de la mise à jour');
        loadEvaluations();
      } catch (error) {
        console.error('Erreur mise à jour:', error);
        loadEvaluationDetail(ev.id);
        alert(t('performance.error_loading') || 'Erreur lors de la mise à jour');
      }
    };

    if (isTextField(field)) {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
      saveDebounceRef.current = setTimeout(doPut, 600);
    } else {
      doPut();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} color="#10B981" />;
      case 'in_progress':
        return <Clock size={16} color="#3B82F6" />;
      case 'cancelled':
        return <XCircle size={16} color="#EF4444" />;
      default:
        return <Clock size={16} color="#6B7280" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: t('performance.status_draft'),
      in_progress: t('performance.status_in_progress'),
      completed: t('performance.status_completed_label'),
      cancelled: t('performance.status_cancelled')
    };
    return labels[status] || status;
  };

  const getRatingLabel = (rating) => {
    const labels = {
      excellent: t('performance.rating_excellent'),
      good: t('performance.rating_good'),
      satisfactory: t('performance.rating_satisfactory'),
      needs_improvement: t('performance.rating_needs_improvement'),
      poor: t('performance.rating_poor')
    };
    return labels[rating] || rating;
  };

  const getRatingColor = (rating) => {
    const colors = {
      excellent: '#10B981',
      good: '#3B82F6',
      satisfactory: '#F59E0B',
      needs_improvement: '#EF4444',
      poor: '#DC2626'
    };
    return colors[rating] || '#6B7280';
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#10B981';
    if (score >= 75) return '#3B82F6';
    if (score >= 60) return '#F59E0B';
    if (score >= 40) return '#EF4444';
    return '#DC2626';
  };

  const getEvaluationTypeLabel = (type) => {
    const labels = {
      annual: t('performance.type_annual'),
      probationary: t('performance.type_probationary'),
      mid_year: t('performance.type_mid_year'),
      project: t('performance.type_project'),
      custom: t('performance.type_custom')
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="performance-container">
        <div className="loading">{t('performance.loading')}</div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? '' : 'performance-container'}>
      {!hideHeader && (
      <div className="objectives-header">
        <div>
          <h1><FileText size={32} /> {t('performance.evaluations_title')}</h1>
          <p>{t('performance.evaluations_subtitle')}</p>
        </div>
        {canCreateEvaluation && (
          <button className="btn-primary" onClick={() => setShowNewModal(true)}>
            <Plus size={20} /> {t('performance.new_evaluation')}
          </button>
        )}
      </div>
      )}
      
      {/* Bouton Nouveau (si header masqué) */}
      {hideHeader && user && canCreateEvaluation && (
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-primary" onClick={() => setShowNewModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={20} /> {t('performance.new_evaluation')}
          </button>
        </div>
      )}
      
      {/* Filtres */}
      <div className="filters-section">
        <select
          value={filters.evaluation_type}
          onChange={(e) => setFilters({ ...filters, evaluation_type: e.target.value })}
        >
          <option value="">{t('performance.all_types')}</option>
          <option value="annual">{t('performance.type_annual')}</option>
          <option value="probationary">{t('performance.type_probationary')}</option>
          <option value="mid_year">{t('performance.type_mid_year')}</option>
          <option value="project">{t('performance.type_project')}</option>
          <option value="custom">{t('performance.type_custom')}</option>
        </select>
        
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">{t('performance.all_statuses')}</option>
          <option value="draft">{t('performance.status_draft')}</option>
          <option value="in_progress">{t('performance.status_in_progress')}</option>
          <option value="completed">{t('performance.status_completed_label')}</option>
        </select>
      </div>

      {/* Liste des évaluations */}
      <div className="objectives-grid">
        {evaluations.length === 0 ? (
          <div className="empty-state">
            <FileText size={64} color="#9CA3AF" />
            <p>{t('performance.no_evaluations')}</p>
          </div>
        ) : (
          evaluations.map((evaluation) => (
            <div key={evaluation.id} className="objective-card" onClick={() => loadEvaluationDetail(evaluation.id)} style={{ cursor: 'pointer' }}>
              <div className="objective-header">
                <div>
                  <h3>{evaluation.title}</h3>
                  <p className="objective-meta">
                    {evaluation.employee_first_name} {evaluation.employee_last_name}
                    {evaluation.department && ` • ${evaluation.department}`}
                  </p>
                </div>
                <div className="objective-status">
                  {getStatusIcon(evaluation.status)}
                  <span>{getStatusLabel(evaluation.status)}</span>
                </div>
              </div>

              {evaluation.description && (
                <p className="objective-description">{evaluation.description}</p>
              )}

              <div className="objective-info">
                <div className="info-item">
                  <Calendar size={16} />
                  <span>{getEvaluationTypeLabel(evaluation.evaluation_type)}</span>
                </div>
                <div className="info-item">
                  <User size={16} />
                  <span>{evaluation.evaluator_first_name} {evaluation.evaluator_last_name}</span>
                </div>
              </div>

              {evaluation.overall_score !== null && (
                <div className="objective-progress">
                  <div className="progress-header">
                    <span>{t('performance.overall_score')}</span>
                    <span style={{ color: getScoreColor(evaluation.overall_score), fontWeight: 700, fontSize: '18px' }}>
                      {(Number(evaluation.overall_score) || 0).toFixed(1)}/100
                    </span>
                  </div>
                  {evaluation.overall_rating && (
                    <div style={{ marginTop: '8px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: getRatingColor(evaluation.overall_rating) + '20',
                        color: getRatingColor(evaluation.overall_rating),
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {getRatingLabel(evaluation.overall_rating)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="objective-info" style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                <span>
                  {new Date(evaluation.evaluation_period_start).toLocaleDateString('fr-FR')} - {new Date(evaluation.evaluation_period_end).toLocaleDateString('fr-FR')}
                </span>
              </div>

              {/* Bouton PDF */}
              {evaluation.status === 'completed' && (
                <div style={{ marginTop: '12px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const token = localStorage.getItem('token');
                      fetch(`${API_BASE}/api/performance/evaluations/${evaluation.id}/pdf`, {
                        headers: { Authorization: `Bearer ${token}` }
                      })
                        .then(res => res.blob())
                        .then(blob => {
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `evaluation-${evaluation.id}.pdf`;
                          link.click();
                          window.URL.revokeObjectURL(url);
                        })
                        .catch(err => {
                          console.error('Erreur téléchargement PDF:', err);
                          alert(t('performance.pdf_download_error'));
                        });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      background: 'rgba(59, 130, 246, 0.2)',
                      border: '1px solid rgba(59, 130, 246, 0.4)',
                      borderRadius: '8px',
                      color: '#3B82F6',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                    }}
                  >
                    <Download size={14} />
                    {t('performance.download_pdf')}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal Nouvelle Évaluation */}
      {showNewModal && (
        <div className="modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('performance.create_evaluation')}</h2>
            
            <div className="form-group">
              <label>{t('performance.employee_label') || 'Employé *'}</label>
              <select
                value={newEvaluation.employee_id}
                onChange={(e) => setNewEvaluation({ ...newEvaluation, employee_id: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              >
                <option value="" style={{ background: '#1e293b', color: '#fff' }}>
                  {t('performance.select_employee') || 'Sélectionner un employé'}
                </option>
                {Array.isArray(employees) && employees.length > 0 ? (
                  employees.map((emp) => (
                    <option key={emp.id} value={emp.id} style={{ background: '#1e293b', color: '#fff' }}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))
                ) : (
                  <option value="" style={{ background: '#1e293b', color: '#fff' }} disabled>
                    {loading ? 'Chargement...' : 'Aucun employé disponible'}
                  </option>
                )}
              </select>
              {!loading && Array.isArray(employees) && employees.length === 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  Aucun employé trouvé. Vérifiez que des employés sont créés dans le module Gestion des Employés.
                </div>
              )}
            </div>

            <div className="form-group">
              <label>{t('performance.evaluation_type_label')}</label>
              <select
                value={newEvaluation.evaluation_type}
                onChange={(e) => setNewEvaluation({ ...newEvaluation, evaluation_type: e.target.value })}
              >
                <option value="annual">{t('performance.type_annual')}</option>
                <option value="probationary">{t('performance.type_probationary')}</option>
                <option value="mid_year">{t('performance.type_mid_year')}</option>
                <option value="project">{t('performance.type_project')}</option>
                <option value="custom">{t('performance.type_custom')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('performance.title_label')}</label>
              <input
                type="text"
                value={newEvaluation.title}
                onChange={(e) => setNewEvaluation({ ...newEvaluation, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('performance.description_label')}</label>
              <textarea
                value={newEvaluation.description}
                onChange={(e) => setNewEvaluation({ ...newEvaluation, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('performance.period_start_label')}</label>
                <input
                  type="date"
                  value={newEvaluation.evaluation_period_start}
                  onChange={(e) => setNewEvaluation({ ...newEvaluation, evaluation_period_start: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('performance.period_end_label')}</label>
                <input
                  type="date"
                  value={newEvaluation.evaluation_period_end}
                  onChange={(e) => setNewEvaluation({ ...newEvaluation, evaluation_period_end: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('performance.interview_date_label')}</label>
              <input
                type="date"
                value={newEvaluation.evaluation_date}
                onChange={(e) => setNewEvaluation({ ...newEvaluation, evaluation_date: e.target.value })}
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowNewModal(false)}>
                {t('performance.cancel')}
              </button>
              <button className="btn-primary" onClick={handleCreateEvaluation} disabled={createLoading}>
                {createLoading ? <><Loader2 size={18} className="spin" /> {t('performance.creating') || 'Creating…'}</> : t('performance.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Détail Évaluation - fermeture uniquement via bouton pour éviter fermeture en tapant */}
      {showDetailModal && selectedEvaluation && (
        <div className="modal-overlay">
          <div className="modal-content evaluation-detail-modal" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>{selectedEvaluation.title}</h2>
            
            <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <p style={{ margin: '4px 0', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                    <strong>{t('performance.employee')}:</strong> {selectedEvaluation.employee_first_name} {selectedEvaluation.employee_last_name}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                    <strong>{t('performance.evaluator')}:</strong> {selectedEvaluation.evaluator_first_name} {selectedEvaluation.evaluator_last_name}
                  </p>
                </div>
                {selectedEvaluation.overall_score !== null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '32px', fontWeight: 700, color: getScoreColor(selectedEvaluation.overall_score) }}>
                      {(Number(selectedEvaluation.overall_score) || 0).toFixed(1)}/100
                    </div>
                    {selectedEvaluation.overall_rating && (
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: getRatingColor(selectedEvaluation.overall_rating) + '20',
                        color: getRatingColor(selectedEvaluation.overall_rating),
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {getRatingLabel(selectedEvaluation.overall_rating)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Critères */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3>{t('performance.evaluation_criteria')}</h3>
                {['admin', 'manager'].includes(user?.role) && selectedEvaluation.status !== 'completed' && (
                  <button className="btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }} onClick={() => {
                    const criterionName = prompt(t('performance.criterion_name_prompt'));
                    if (criterionName) {
                      setNewCriterion({ ...newCriterion, criterion_name: criterionName });
                      setTimeout(() => {
                        document.getElementById('add-criterion-section')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}>
                    <Plus size={16} /> {t('performance.add_criterion')}
                  </button>
                )}
              </div>

              {criteria.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px' }}>
                  {t('performance.no_criteria')}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {criteria.map((criterion) => (
                    <div key={criterion.id} style={{
                      padding: '16px',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>{criterion.criterion_name}</h4>
                          {criterion.criterion_category && (
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                              {criterion.criterion_category}
                            </span>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: getScoreColor(criterion.score) }}>
                            {criterion.score}/100
                          </div>
                          {criterion.rating && (
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '8px',
                              backgroundColor: getRatingColor(criterion.rating) + '20',
                              color: getRatingColor(criterion.rating),
                              fontSize: '11px',
                              fontWeight: 600
                            }}>
                              {getRatingLabel(criterion.rating)}
                            </span>
                          )}
                        </div>
                      </div>
                      {criterion.description && (
                        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: '8px 0' }}>
                          {criterion.description}
                        </p>
                      )}
                      {criterion.evaluator_notes && (
                        <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', margin: 0 }}>
                            <strong>Notes:</strong> {criterion.evaluator_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Ajouter un critère */}
            {['admin', 'manager'].includes(user?.role) && selectedEvaluation.status !== 'completed' && (
              <div id="add-criterion-section" style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '12px',
                border: '1px dashed rgba(255,255,255,0.2)'
              }}>
                <h4 style={{ marginBottom: '16px' }}>{t('performance.add_criterion_button')}</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('performance.criterion_name_label')}</label>
                    <input
                      type="text"
                      value={newCriterion.criterion_name}
                      onChange={(e) => setNewCriterion({ ...newCriterion, criterion_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('performance.criterion_category_label')}</label>
                    <select
                      value={newCriterion.criterion_category}
                      onChange={(e) => setNewCriterion({ ...newCriterion, criterion_category: e.target.value })}
                    >
                      <option value="technical">{t('performance.category_technical')}</option>
                      <option value="soft_skills">{t('performance.category_soft_skills')}</option>
                      <option value="leadership">{t('performance.category_leadership')}</option>
                      <option value="communication">{t('performance.category_communication')}</option>
                      <option value="other">{t('performance.category_other')}</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('performance.description_label')}</label>
                  <textarea
                    value={newCriterion.description}
                    onChange={(e) => setNewCriterion({ ...newCriterion, description: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('performance.score_label')}</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={newCriterion.score}
                      onChange={(e) => {
                        const score = parseInt(e.target.value) || 0;
                        let rating = '';
                        if (score >= 90) rating = 'excellent';
                        else if (score >= 75) rating = 'good';
                        else if (score >= 60) rating = 'satisfactory';
                        else if (score >= 40) rating = 'needs_improvement';
                        else rating = 'poor';
                        setNewCriterion({ ...newCriterion, score, rating });
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('performance.weight_label')}</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      value={newCriterion.weight}
                      onChange={(e) => setNewCriterion({ ...newCriterion, weight: parseFloat(e.target.value) || 1.0 })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('performance.evaluator_notes_label')}</label>
                  <textarea
                    value={newCriterion.evaluator_notes}
                    onChange={(e) => setNewCriterion({ ...newCriterion, evaluator_notes: e.target.value })}
                    rows={2}
                  />
                </div>

                <button className="btn-primary" onClick={handleAddCriterion} style={{ width: '100%' }}>
                  {t('performance.add_criterion_button')}
                </button>
              </div>
            )}

            {/* Commentaires */}
            <div style={{ marginBottom: '24px' }}>
              <h3>{t('performance.comments_label')}</h3>
              
              {user?.role === 'employee' ? (
                <div className="form-group">
                  <label>{t('performance.self_assessment_label')}</label>
                  <textarea
                    value={selectedEvaluation.employee_self_assessment || ''}
                    onChange={(e) => handleUpdateEvaluation('employee_self_assessment', e.target.value)}
                    rows={4}
                    placeholder={t('performance.self_assessment_placeholder')}
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>{t('performance.evaluator_comments_label')}</label>
                  <textarea
                    value={selectedEvaluation.evaluator_comments || ''}
                    onChange={(e) => handleUpdateEvaluation('evaluator_comments', e.target.value)}
                    rows={4}
                    placeholder={t('performance.evaluator_comments_placeholder')}
                  />
                </div>
              )}

              {selectedEvaluation.employee_comments && (
                <div className="form-group">
                  <label>{t('performance.employee_comments_label')}</label>
                  <textarea
                    value={selectedEvaluation.employee_comments}
                    onChange={(e) => handleUpdateEvaluation('employee_comments', e.target.value)}
                    rows={3}
                    readOnly={user?.role === 'employee'}
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('performance.action_plan_label')}</label>
                <textarea
                  value={selectedEvaluation.action_plan || ''}
                  onChange={(e) => handleUpdateEvaluation('action_plan', e.target.value)}
                  rows={3}
                  placeholder={t('performance.action_plan_placeholder')}
                  readOnly={user?.role === 'employee'}
                />
              </div>
            </div>

            {/* Actions: Close pour tous; statut uniquement admin/manager */}
            <div className="modal-actions">
              {['admin', 'manager'].includes(user?.role) && selectedEvaluation.status !== 'completed' && (
                <label className="evaluation-status-label">
                  <span className="evaluation-status-label-text">{t('performance.status_label') || 'Status'}:</span>
                  <select
                    className="evaluation-status-select"
                    value={selectedEvaluation.status}
                    onChange={(e) => handleUpdateEvaluation('status', e.target.value)}
                    aria-label={t('performance.status_label') || 'Status'}
                  >
                    <option value="draft">{t('performance.status_draft')}</option>
                    <option value="in_progress">{t('performance.status_in_progress')}</option>
                    <option value="completed">{t('performance.status_completed_label')}</option>
                  </select>
                </label>
              )}
              <button className="btn-secondary" onClick={() => {
                setShowDetailModal(false);
                setSelectedEvaluation(null);
                setCriteria([]);
              }}>
                {t('performance.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EvaluationsList;

